import type { CSSProperties } from 'react';
import { VehicleArt } from './Vehicle';

/**
 * Small empty-state drawings (vector, theme tokens): no trips yet, nothing found, no drivers nearby, a trip link
 * that has ended. 160×110, they float gently (off with reduced motion).
 */
export type EmptyKind = 'trips' | 'search' | 'drivers' | 'ended';

export function EmptyArt({ kind, width = 160, style }: { kind: EmptyKind; width?: number; style?: CSSProperties }) {
  const h = Math.round((width * 110) / 160);
  return (
    <svg aria-hidden="true" width={width} height={h} viewBox="0 0 160 110" style={{ display: 'block', overflow: 'visible', ...style }}>
      <ellipse cx="80" cy="100" rx="62" ry="6" fill="var(--s2)" />
      {kind === 'trips' && (
        <g>
          <path d="M18 92Q60 70 84 74Q112 78 142 50" fill="none" stroke="var(--s3)" strokeWidth="10" strokeLinecap="round" />
          <path d="M18 92Q60 70 84 74Q112 78 142 50" fill="none" stroke="var(--acText)" strokeWidth="2.4" strokeLinecap="round" strokeDasharray="2 8" />
          <circle cx="18" cy="92" r="6" fill="var(--bg)" stroke="var(--acText)" strokeWidth="3.4" />
          <g className="zr-float">
            <path d="M142 48s-13-11-13-21a13 13 0 1126 0c0 10-13 21-13 21z" fill="var(--pri)" />
            <circle cx="142" cy="27" r="5" fill="var(--priInk)" />
          </g>
          <g transform="translate(40 50)">
            <VehicleArt type="AUTO" width={58} shadow={false} />
          </g>
        </g>
      )}
      {kind === 'search' && (
        <g>
          <rect x="22" y="30" width="96" height="62" rx="12" fill="var(--s2)" />
          <path d="M22 62H118M70 30V92" stroke="var(--s3)" strokeWidth="5" />
          <path d="M70 66s-10-8.5-10-16a10 10 0 1120 0c0 7.5-10 16-10 16z" fill="var(--ink3)" />
          <g className="zr-float">
            <circle cx="112" cy="40" r="20" fill="var(--bg)" stroke="var(--acText)" strokeWidth="5" />
            <path d="M126 55l14 14" stroke="var(--acText)" strokeWidth="7" strokeLinecap="round" />
          </g>
        </g>
      )}
      {kind === 'drivers' && (
        <g>
          <g transform="translate(26 30)">
            <VehicleArt type="AUTO" width={100} riders={false} shadow={false} />
          </g>
          <g className="zr-float" fill="var(--ink3)" style={{ fontFamily: 'Satoshi, system-ui, sans-serif', fontWeight: 900 }}>
            <text x="118" y="30" fontSize="16">z</text>
            <text x="130" y="18" fontSize="12">z</text>
            <text x="140" y="9" fontSize="9">z</text>
          </g>
        </g>
      )}
      {kind === 'ended' && (
        <g>
          <path d="M40 92V22" stroke="var(--ink3)" strokeWidth="4" strokeLinecap="round" />
          <g className="zr-flag">
            <path d="M42 24H96L86 40L96 56H42Z" fill="var(--s3)" />
            <path d="M42 24H56V36H42ZM70 24H84V36H70ZM56 36H70V48H56ZM84 36H90L86 40L90 48H84Z" fill="var(--ink)" opacity="0.85" />
          </g>
          <circle cx="116" cy="74" r="20" fill="var(--acSoft)" />
          <path d="M106 74l7 7 13-14" fill="none" stroke="var(--acText)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      )}
    </svg>
  );
}
